import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, CalendarCheck, Clock, RefreshCw, Phone } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND, buildWhatsappLink } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex items-center gap-4">
      <div className="h-12 w-12 rounded-xl bg-efory-blue/15 flex items-center justify-center text-efory-blue">
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <div className="font-heading text-2xl font-extrabold text-white">{value}</div>
        <div className="text-sm text-white/55">{label}</div>
      </div>
    </div>
  );
}

const fmt = (iso) => {
  try {
    return format(new Date(iso), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "—";
  }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState({ leads: 0, schedules: 0, pending_schedules: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s, st] = await Promise.all([
        api.get("/leads"),
        api.get("/schedules"),
        api.get("/stats"),
      ]);
      setLeads(l.data);
      setSchedules(s.data);
      setStats(st.data);
    } catch (_) {
      /* handled by interceptor / redirect */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-efory-ink">
      {/* Header */}
      <header className="bg-efory-ink text-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="EFORY" className="h-9 w-9 object-contain rounded-md" />
            <div>
              <span className="font-heading font-extrabold tracking-[0.2em] block leading-none">EFORY</span>
              <span className="text-xs text-white/50">Painel de contatos</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/60 hidden sm:block">{user?.email}</span>
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              data-testid="refresh-button"
              className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              onClick={doLogout}
              variant="outline"
              size="sm"
              data-testid="logout-button"
              className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Cadastros recebidos" value={stats.leads} testid="stat-leads" />
          <StatCard icon={CalendarCheck} label="Agendamentos" value={stats.schedules} testid="stat-schedules" />
          <StatCard icon={Clock} label="Pendentes" value={stats.pending_schedules} testid="stat-pending" />
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="bg-white/[0.03] border border-white/10">
            <TabsTrigger value="leads" data-testid="tab-leads" className="text-white/60 data-[state=active]:text-[#0F172A]">Cadastros ({leads.length})</TabsTrigger>
            <TabsTrigger value="schedules" data-testid="tab-schedules" className="text-white/60 data-[state=active]:text-[#0F172A]">Agendamentos ({schedules.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden text-white">
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow className="bg-white/5 border-white/10 hover:bg-white/5">
                    <TableHead className="text-white/50">Nome</TableHead>
                    <TableHead className="text-white/50">CPF</TableHead>
                    <TableHead className="text-white/50">Telefone</TableHead>
                    <TableHead className="text-white/50">Recebido em</TableHead>
                    <TableHead className="text-right text-white/50">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">Carregando...</TableCell></TableRow>
                  ) : leads.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">Nenhum cadastro ainda.</TableCell></TableRow>
                  ) : (
                    leads.map((l) => (
                      <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="border-white/10 hover:bg-white/5">
                        <TableCell className="font-medium text-white">{l.full_name}</TableCell>
                        <TableCell className="text-white/80">{l.cpf}</TableCell>
                        <TableCell className="text-white/80">{l.phone}</TableCell>
                        <TableCell className="text-white/50">{fmt(l.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <a href={buildWhatsappLink(`Olá ${l.full_name}, aqui é da EFORY!`)} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="text-[#25D366] hover:bg-[#25D366]/10">
                              <Phone className="h-4 w-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="schedules" className="mt-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden text-white">
              <Table data-testid="schedules-table">
                <TableHeader>
                  <TableRow className="bg-white/5 border-white/10 hover:bg-white/5">
                    <TableHead className="text-white/50">Nome</TableHead>
                    <TableHead className="text-white/50">Telefone</TableHead>
                    <TableHead className="text-white/50">Data</TableHead>
                    <TableHead className="text-white/50">Horário</TableHead>
                    <TableHead className="text-white/50">Status</TableHead>
                    <TableHead className="text-right text-white/50">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Carregando...</TableCell></TableRow>
                  ) : schedules.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Nenhum agendamento ainda.</TableCell></TableRow>
                  ) : (
                    schedules.map((s) => (
                      <TableRow key={s.id} data-testid={`schedule-row-${s.id}`} className="border-white/10 hover:bg-white/5">
                        <TableCell className="font-medium text-white">{s.name}</TableCell>
                        <TableCell className="text-white/80">{s.phone}</TableCell>
                        <TableCell className="text-white/80">{(() => { try { return format(new Date(s.date + "T00:00:00"), "dd/MM/yyyy"); } catch { return s.date; } })()}</TableCell>
                        <TableCell className="text-white/80">{s.time}</TableCell>
                        <TableCell><Badge className="bg-efory-blue/10 text-efory-blue hover:bg-efory-blue/10">{s.status}</Badge></TableCell>
                        <TableCell className="text-right">
                          <a href={buildWhatsappLink(`Olá ${s.name}, confirmando seu agendamento na EFORY.`)} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="text-[#25D366] hover:bg-[#25D366]/10">
                              <Phone className="h-4 w-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
